import React from 'react';
import {
  formatProgramLearningBatchItemMeta,
  formatProgramLearningBatchItemTitle,
  formatProgramLearningBatchTermProgramLabel,
  type ProgramLearningBatchGenerateItem,
  type ProgramLearningBatchGenerateResult,
} from '../../utils/programLearningBatchGenerate';

type TrackLearningBatchGenerateResultModalProps = {
  result: ProgramLearningBatchGenerateResult;
  onClose: () => void;
};

function groupItemsByTermProgram(items: ProgramLearningBatchGenerateItem[]) {
  return ([1, 2] as const)
    .map((termNumber) => ({
      termNumber,
      label: formatProgramLearningBatchTermProgramLabel(termNumber),
      items: items.filter((item) => item.termNumber === termNumber),
    }))
    .filter((group) => group.items.length > 0);
}

function ResultSection({
  title,
  items,
}: {
  title: string;
  items: ProgramLearningBatchGenerateItem[];
}) {
  if (!items.length) return null;
  const groups = groupItemsByTermProgram(items);
  return (
    <section className="cp-track-learning-batch-result-section">
      <h4>
        {title} <span>{items.length}건</span>
      </h4>
      {groups.map((group) => (
        <div key={group.termNumber} className="cp-track-learning-batch-result-term-group">
          <h5 className="cp-track-learning-batch-result-term-heading">{group.label}</h5>
          <ul className="cp-track-learning-batch-result-list">
            {group.items.map((item, index) => {
              const meta = formatProgramLearningBatchItemMeta(item);
              return (
                <li
                  key={`${item.kind}-${item.gradeLabel}-${item.trackName}-${item.scopeLabel}-${item.termNumber}-${index}`}
                  className="cp-track-learning-batch-result-item"
                >
                  <div className="cp-track-learning-batch-result-item-head">
                    <div className="cp-track-learning-batch-result-item-title">
                      {formatProgramLearningBatchItemTitle(item)}
                    </div>
                    <span className="cp-track-learning-batch-result-item-term">{item.termLabel}</span>
                  </div>
                  {meta ? <div className="cp-track-learning-batch-result-item-meta">{meta}</div> : null}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}

const TrackLearningBatchGenerateResultModal: React.FC<TrackLearningBatchGenerateResultModalProps> = ({
  result,
  onClose,
}) => {
  const createdCount = result.created.length;
  const skippedExistingCount = result.skippedExisting.length;
  const skippedEmptyCount = result.skippedEmpty.length;
  const hasAny = createdCount + skippedExistingCount + skippedEmptyCount > 0;

  return (
    <div className="cp-modal-backdrop" onClick={onClose}>
      <div
        className="cp-modal cp-track-learning-batch-result-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <h3>학습내용 일괄 생성 결과</h3>
        <p className="page-desc">
          학습내용이 비어 있는 프로그램 테이블에만 생성했습니다. 이미 내용이 있는 표는 덮어쓰지
          않았습니다.
        </p>
        <div className="cp-modal-body">
          <p className="cp-track-learning-batch-result-summary">
            생성 {createdCount}건 · 기존 데이터로 건너뜀 {skippedExistingCount}건 · 관리자 데이터 없음{' '}
            {skippedEmptyCount}건
          </p>
          {hasAny ? (
            <>
              <ResultSection title="생성됨" items={result.created} />
              <ResultSection title="기존 학습내용이 있어 건너뜀" items={result.skippedExisting} />
              <ResultSection title="관리자 학습내용이 없어 건너뜀" items={result.skippedEmpty} />
            </>
          ) : (
            <p className="page-desc">생성하거나 건너뛴 대상이 없습니다.</p>
          )}
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-btn-primary" onClick={onClose}>
            확인
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrackLearningBatchGenerateResultModal;
